/**
 * The Drop Hub's Stage, as a hook.
 *
 * Extracted from `DropHubSurface.tsx`, which had grown to 528 lines against
 * this project's 400-line ceiling — the same ceiling its own header cites as
 * the reason it was split out of `DropPage` in the first place. Stage state is
 * the most self-contained thing in that file: it owns two pieces of state, one
 * effect and one resolver call, and nothing else in the page reads them except
 * to render.
 *
 * It holds no markup and makes no layout decision. `resolveStage` decides what
 * the Stage is showing and how big it is; this hook only owns the local state
 * a page needs to drive it — what the viewer selected, and whether they asked
 * to expand it.
 */

import React from "react";
import {type SurfaceStageOccupant} from "@/surfaces/spine/SurfaceStage";
import {resolveStage, StageOccupantClass, StageState, type ResolvedStage} from "@/surfaces/stageOccupant";

export interface DropHubStage {
    /** What the viewer selected, in the shape `SurfaceStage` renders. */
    occupant: SurfaceStageOccupant | null;
    setOccupant: React.Dispatch<React.SetStateAction<SurfaceStageOccupant | null>>;
    /** The resolved Stage — occupant, state, and whether the column is chat. */
    resolved: ResolvedStage;
    /**
     * The state to hand `SurfaceStructure`, which publishes it as `data-stage`.
     * The same value as `resolved.state` — named separately only because that
     * is the prop's job, and the page previously failed to pass it at all.
     */
    structureStage: StageState;
}

export interface DropHubStageInput {
    /**
     * What holds the Stage when nothing is playing.
     *
     * The Drop Hub had no non-streaming occupant at all: `resolveStage` was
     * called with only `streaming`, so an idle Stage resolved to
     * `occupant: null` and the surface fell back to whatever `SurfaceStage`
     * drew from its own props. The class existed and nothing was ever put in
     * it.
     *
     * Omitted still means exactly that — a Hub that supplies no occupant keeps
     * today's behaviour rather than gaining a placeholder.
     */
    idle?: React.ReactNode;
    /** Identity for the idle occupant, so a swap is distinguishable from a re-render. */
    idleId?: string;
}

export function useDropHubStage(input: DropHubStageInput = {}): DropHubStage {
    const {idle, idleId = "hub-idle"} = input;
    const [occupant, setOccupant] = React.useState<SurfaceStageOccupant | null>(null);

    /*
     * Expansion is DERIVED, never requested: a live occupant expands on
     * arrival (anchor contract — there is no viewer-facing expand control),
     * and the shared resolver still gates it on `canExpand`.
     */
    const [expandRequested, setExpandRequested] = React.useState(false);

    /* Recomputed per occupant IDENTITY, so no state outlives the stream it described. */
    const occupantKey = occupant?.title ?? null;
    const occupantLive = Boolean(occupant?.live);
    React.useEffect(() => {
        /* A live stream expands on arrival (anchor contract); anything else starts compact. */
        setExpandRequested(occupantLive);
    }, [occupantKey, occupantLive]);

    /*
     * THE STAGE, RESOLVED ONCE — and told to the STRUCTURE, not only the stage.
     *
     * `SurfaceStructure` takes a `stage` prop and publishes it as `data-stage`,
     * which is what `HubStructure.scss` and `HubSpine.scss` key their EXPANDED
     * rules on. The page never passed it, so the attribute sat at its `IDLE`
     * default forever and the stage could not grow even if some other code had
     * produced the state. That was the second half of why EXPANDED was
     * unreachable: one half was the page's local `occupant ? "WATCHING" :
     * "IDLE"` ternary, the other was the structure never being told at all.
     */
    const resolved = React.useMemo(() => resolveStage({
        streaming: occupant
            ? {
                kind: StageOccupantClass.STREAMING,
                id: occupant.title,
                player: occupant.player,
                imageUrl: occupant.imageUrl,
                live: Boolean(occupant.live),
                viewers: occupant.viewers,
                title: occupant.title,
                subtitle: occupant.subtitle,
                tags: occupant.tags,
                /*
                 * EXPANDED exists to make room for chat — a live stream with no
                 * resolvable channel must not take the chat geometry only to
                 * render featured cards squeezed inside it.
                 */
                canExpand: Boolean(occupant.chat),
            }
            : null,
        idle: idle
            ? {kind: StageOccupantClass.NON_STREAMING, id: idleId, widget: idle}
            : null,
        expandRequested,
    }), [occupant, expandRequested, idle, idleId]);

    return {
        occupant,
        setOccupant,
        resolved,
        /*
         * No translation. `resolveStage` returns `occupancy.ts`'s own
         * `StageState`, which is exactly what `SurfaceStructure` publishes —
         * there was a three-branch ternary converting between two spellings of
         * the same three states, which is the drift a single vocabulary
         * removes.
         */
        structureStage: resolved.state,
    };
}
